import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import UMDLogo from "./UMDLogo";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/projects", label: "Projects" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/team", label: "Team" },
  { to: "/insights", label: "Insights" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-[var(--border)]" : "bg-transparent"
      }`}
    >
      <div className="container-wide flex items-center justify-between py-4 md:py-5">
        <UMDLogo light={!scrolled} />

        <nav className="hidden xl:flex items-center gap-7">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            const onDarkHero = !scrolled;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[13px] font-semibold tracking-wide transition-colors link-u whitespace-nowrap ${
                  onDarkHero ? "text-white/90 hover:text-white" : "text-[#0a0a0a] hover:text-[var(--primary)]"
                } ${active ? "text-[var(--primary)]" : ""}`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="btn-primary !py-3 !px-5 !text-[12px] whitespace-nowrap"
          >
            Get a proposal
            <ArrowUpRight size={16} className="arrow" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`xl:hidden p-2 ${!scrolled ? "text-white" : "text-[#0a0a0a]"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`xl:hidden overflow-hidden transition-all duration-500 bg-white border-t border-[var(--border)] ${
          open ? "max-h-[720px]" : "max-h-0"
        }`}
      >
        <div className="container-wide py-6 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="py-3 text-[15px] font-semibold border-b border-[var(--border)] flex items-center justify-between"
            >
              {l.label}
              <ArrowUpRight size={18} className="text-[var(--muted-fg)]" />
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-cta-contact"
            className="btn-primary mt-6 justify-center"
          >
            Get a proposal <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </header>
  );
}
